import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { TabPanel } from 'react-tabs';
import { colors } from '../../../../../../../../../../colorVars';
import { addExitTactic } from '../../../../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/addExitTactic';
import { useWebsocketStore } from '../../../../../../../../../../stores/websocketStore/websocketProvider';
import { MultiSwitch } from '../../../../../../../../../atomic/buttons/multiSwitch/MultiSwitch';
import TextButton from '../../../../../../../../../atomic/buttons/textButton/TextButton';
import { EditableTableFieldNoOnBlurUpdate } from '../../../../../../../../../atomic/inputs/editableTableField/EditableFieldNoOnBlurUpdate';
import { TableSelect } from '../../../../../../../../../atomic/inputs/tableSelect/TableSelect';
import { Body3 } from '../../../../../../../../../atomic/text/Text';
import { mfeRetracementValidation } from '../../../../../../../../../forms/tradeplans/AddTradePlan/DataValidation';
import { IndividualPanelHeader } from '../../../../individualTacticPanels/IndividualPanelHeader';
import style from './IndividualAddTacticPanel.module.css';

export const AddMfeRetracementTacticPanel = observer(
  // eslint-disable-next-line no-unused-vars
  ({ tradePlanId, softOrderId, addTab, ...rest }) => {
    const { tradePlanStore } = useWebsocketStore();
    const tradePlan = tradePlanStore.tradePlans[tradePlanId];
    const [headerText, setHeaderText] = useState('Add MFE Retracement');
    const [headerTextColor, setHeaderTextColor] = useState(colors.lightGrey);
    const [details, setDetails] = useState({
      mfeType: 'byPositionPrice', //drop down options: byPositionPrice, byPnl
      retraceType: 'expanding', //dont show to users, this is for future implementations
      activateSetting: 'PERCENT', //options: PERCENT, AMOUNT. notes: If mfeType === position price, this must be a percentage. if mfeType==byPnl, this can be either pct or amount
      pnlType: 'OVERALL', //options: OVERALL, UNREALIZED. notes: only selectable if mfeType===byPnl
      retracePct: 0, //pct to retrace from max mfe
      activationValue: 0,
    });
    const timeoutRef = useRef(null);
    const addTactic = () => {
      const detailsCopy = cloneDeep(details);
      detailsCopy.retracePct = Number(details.retracePct) / 100;
      // if (details.mfeType === 'byPositionPrice')
      //   detailsCopy.activateSetting = 'PERCENT';
      if (detailsCopy.activateSetting === 'PERCENT')
        detailsCopy.activationValue = details.activationValue / 100;

      const validationResult = mfeRetracementValidation(detailsCopy);
      if (validationResult[0] === false) {
        setHeaderText(validationResult[1]);
        setHeaderTextColor(colors.red);
        const timer = setTimeout(() => {
          setHeaderText('Add MFE Retracement');
          setHeaderTextColor(colors.lightGrey);
        }, 2000);
        timeoutRef.current = timer;
      } else {
        addExitTactic(tradePlanId, {
          tactic: 'MFE_RETRACEMENT',
          details: detailsCopy,
        });
        setHeaderText('MFE Retracement Added');
        setHeaderTextColor(colors.green);
        const timer = setTimeout(() => {
          setHeaderText('Add MFE Retracement');
          setHeaderTextColor(colors.lightGrey);
        }, 2000);
        timeoutRef.current = timer;
      }
    };

    useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, []);

    return (
      <TabPanel {...rest}>
        <div className={style.tableContainer}>
          <table className={style.table}>
            <IndividualPanelHeader />
            <tbody>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    MFE Type
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <MultiSwitch
                    margin="6px 0 0 0"
                    labels={['Position', 'PnL']}
                    values={['byPositionPrice', 'byPnl']}
                    controlledValue={details.mfeType}
                    setValue={(value) =>
                      setDetails((prev) => ({ ...prev, mfeType: value }))
                    }
                  />
                </td>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Activation Setting
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <TableSelect
                    // disabled={details.mfeType === 'byPositionPrice'}
                    color={colors.lightGrey}
                    backgroundColor="inherit"
                    value={details.activateSetting}
                    onChange={(newValue) =>
                      setDetails((prev) => ({
                        ...prev,
                        activateSetting: newValue,
                      }))
                    }
                    options={[
                      { label: 'Percent', value: 'PERCENT' },
                      { label: 'Dollars', value: 'AMOUNT' },
                    ]}
                  />
                </td>
              </tr>
              <tr>
                <td className={style.labelTd}>
                  <Body3
                    fontWeight={700}
                    color={
                      details.mfeType === 'byPositionPrice'
                        ? 'gray'
                        : colors.lightGrey
                    }
                  >
                    PnL Type
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <TableSelect
                    disabled={details.mfeType === 'byPositionPrice'}
                    color={colors.lightGrey}
                    backgroundColor="inherit"
                    value={details.pnlType}
                    onChange={(newValue) =>
                      setDetails((prev) => ({ ...prev, pnlType: newValue }))
                    }
                    options={[
                      { label: 'Overall', value: 'OVERALL' },
                      { label: 'Unrealized', value: 'UNREALIZED' },
                    ]}
                  />
                </td>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Activation Amount
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  {details.activateSetting === 'AMOUNT' ? (
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      $
                    </Body3>
                  ) : null}
                  <EditableTableFieldNoOnBlurUpdate
                    inputType="number"
                    bodySize={3}
                    value={details.activationValue}
                    setValue={(newValue) =>
                      setDetails((prev) => ({
                        ...prev,
                        activationValue: newValue,
                      }))
                    }
                    color={colors.lightGrey}
                    backgroundColor="inherit"
                  />
                  {details.activateSetting === 'PERCENT' ? (
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      %
                    </Body3>
                  ) : null}
                </td>
              </tr>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Retrace %
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <EditableTableFieldNoOnBlurUpdate
                    inputType="number"
                    bodySize={3}
                    value={details.retracePct}
                    setValue={(newValue) =>
                      setDetails((prev) => ({
                        ...prev,
                        retracePct: newValue,
                      }))
                    }
                    color={colors.lightGrey}
                    backgroundColor="inherit"
                  />
                  <Body3
                    fontWeight={700}
                    color={colors.lightGrey}
                    margin="3px 3px 0 0"
                  >
                    %
                  </Body3>
                </td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <header className={style.header}>
            <Body3 color={headerTextColor} fontWeight={700}>
              {headerText}
            </Body3>
            <TextButton
              disabled={tradePlan.exitTactics['MFE_RETRACEMENT']}
              color={colors.lightGrey}
              backgroundColor={
                tradePlan.exitTactics['MFE_RETRACEMENT']
                  ? colors.grey
                  : colors.blue
              }
              fontSize="small"
              clickHandler={addTactic}
              width="20%"
              height="50%"
            >
              Add
            </TextButton>
          </header>
        </div>
      </TabPanel>
    );
  }
);

AddMfeRetracementTacticPanel.tabsRole = 'TabPanel';
